import React, { useEffect, useRef } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useFormatLastUpdate } from "@/utilities";
import { Typography, Flex, theme, Divider, Button } from "antd";
import { ShopOutlined } from "@ant-design/icons";
import { SellerIcon } from "../icon";

const { Text, Link } = Typography;

const QuoteCardComponent = ({
  id,
  name,
  status,
  store,
  shareHash,
  quoteSellers,
  city,
  region,
  updatedAt,
  createdAt,
  participations,
  visits,
}: any) => {
  const { token } = theme.useToken();
  const [showAll, setShowAll] = React.useState(false);
  const leftColRef = useRef<HTMLDivElement>(null);
  const rightColRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const goToQuote = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/quotes/${id}`);
  };

  const totalSum = quoteSellers
    ? quoteSellers.reduce((sum: any, seller: any) => sum + seller.total, 0)
    : 0;

  const formattedTotal = totalSum.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
  });

  const formattedCreateAt = createdAt
    ? dayjs(createdAt).format("DD/MM/YY")
    : "N/A";

  const formatLastUpdate = useFormatLastUpdate();

  const groupParticipants = (
    participants: any[],
    sellerCode: string,
    storeCode: string,
  ) => {
    const usersWithSellerPermissions = participants
      .filter(
        (p) =>
          p.participationType === "seller" &&
          p.participationIdentifier === sellerCode,
      )
      .map((p) => ({ ...p, type: "seller" }));

    const usersWithStorePermissions = participants
      .filter(
        (p) =>
          p.participationType === "store" &&
          p.participationIdentifier === storeCode,
      )
      .map((p) => ({ ...p, type: "store" }));

    const usersWithoutPermissions = participants.filter(
      (p) => !p.participationType || p.participationType === "",
    );

    return {
      usersWithSellerPermissions,
      usersWithStorePermissions,
      usersWithoutPermissions,
    };
  };

  const { usersWithoutPermissions, usersWithStorePermissions } =
    groupParticipants(participations, "", store.code);

  useEffect(() => {
    const syncHeights = () => {
      if (!leftColRef.current || !rightColRef.current) return;

      leftColRef.current.style.minHeight = "auto";
      rightColRef.current.style.minHeight = "auto";

      const leftHeight = leftColRef.current.offsetHeight;
      const rightHeight = rightColRef.current.offsetHeight;
      const maxHeight = Math.max(leftHeight, rightHeight);

      leftColRef.current.style.minHeight = `${maxHeight}px`;
      rightColRef.current.style.minHeight = `${maxHeight}px`;
    };

    const raf = requestAnimationFrame(syncHeights);
    return () => cancelAnimationFrame(raf);
  }, [showAll, quoteSellers.length, participations.length]);

  return (
    <Flex
      vertical
      gap={4}
      style={{
        padding: 16,
        margin: "4px 16px",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        backgroundColor: "white",
        width: 320,
      }}
    >
      {/* Header */}
      <Flex justify="space-between" align="center">
        <Typography.Paragraph
          style={{
            margin: 0,
            maxWidth: 330,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          ellipsis={{
            tooltip: `#${id} ${name}`,
          }}
        >
          <Text strong style={{ color: token.colorLinkHover }}>
            <a
              href={`/quotes/${id}`}
              onClick={goToQuote}
              style={{ color: "inherit" }}
            >{`#${id}`}</a>
          </Text>
        </Typography.Paragraph>
        {formattedCreateAt}
      </Flex>

      {/* Nombre y última actualización */}
      <Flex justify="space-between" align="center">
        <Text strong style={{ color: token.colorLinkHover }}>
          <a
            href={`/quotes/${id}`}
            onClick={goToQuote}
            style={{ color: "inherit" }}
          >{`${name}`}</a>
        </Text>
        <Text className="date-font-12">{formatLastUpdate(updatedAt)}</Text>
      </Flex>

      {/* Región y visitas */}
      <Flex justify="space-between" align="center" className="date-font-12">
        <span>
          {region && (
            <Text
              style={{ color: token.colorTextSecondary }}
            >{`${region}, `}</Text>
          )}
          {city && (
            <Text style={{ color: token.colorTextSecondary }}>{city}</Text>
          )}
        </span>
        <Text style={{ color: token.colorTextSecondary }}>
          Viewed x {visits}
        </Text>
      </Flex>

      {usersWithoutPermissions?.length > 0 && (
        <>
          <Divider style={{ margin: "13px 0 11px" }} />

          {/* Usuarios sin permisos */}
          <Flex justify="space-between">
            <Flex vertical align="start" gap={8}>
              {usersWithoutPermissions
                .slice(0, showAll ? usersWithoutPermissions.length : 1)
                .map((user: any, index: number) => (
                  <Text
                    strong
                    key={`noperm-${user.contactId}-${index}`}
                    style={{ color: token.colorLinkHover }}
                  >
                    <Link href={`/contacts/${user?.contactId}`}>
                      {user.firstname} {user.lastname}
                    </Link>
                  </Text>
                ))}
            </Flex>

            {/* Botón expandir/colapsar */}
            {(quoteSellers?.length > 1 || participations?.length > 2) && (
              <Button size="small" onClick={() => setShowAll(!showAll)}>
                {showAll ? "-" : "+"}
              </Button>
            )}
          </Flex>
        </>
      )}

      <Divider style={{ margin: "13px 0 11px" }} />

      <Flex justify="space-between" align="start" gap={16}>
        {/* Columna izquierda: Store y Sellers */}
        <Flex
          vertical
          align="start"
          gap={8}
          style={{ flex: 1 }}
          ref={leftColRef}
        >
          {/* Store */}
          <Text style={{ color: token.colorTextSecondary }}>
            <ShopOutlined /> {store?.name}
          </Text>

          {/* Sellers */}
          {quoteSellers?.length > 0 &&
            quoteSellers
              .slice(0, showAll ? quoteSellers.length : 1)
              .map((seller: any, index: number) => (
                <Text
                  key={`seller-${index}`}
                  style={{ color: token.colorTextSecondary }}
                >
                  <SellerIcon /> {seller.sellerCode}
                </Text>
              ))}
        </Flex>

        {/* Columna derecha: Participantes */}
        <Flex
          vertical
          align="end"
          gap={4}
          style={{ flex: 1, textAlign: "right" }}
          ref={rightColRef}
        >
          {usersWithStorePermissions.length > 0 &&
            usersWithStorePermissions
              .slice(0, showAll ? usersWithStorePermissions.length : 1)
              .map((user: any, index: number) => (
                <Text
                  key={`storeperm-${user.contactId}-${index}`}
                  style={{ color: token.colorTextSecondary }}
                >
                  {user.firstname} {user.lastname}
                </Text>
              ))}

          {quoteSellers?.length > 0 &&
            quoteSellers.map((seller: any, sIndex: number) => {
              const { usersWithSellerPermissions } = groupParticipants(
                participations,
                seller.sellerCode,
                store.code,
              );

              return usersWithSellerPermissions
                .slice(0, showAll ? usersWithSellerPermissions.length : 1)
                .map((user: any, index: number) => (
                  <Text
                    key={`sellerperm-${user.contactId}-${sIndex}-${index}`}
                    style={{ color: token.colorTextSecondary }}
                  >
                    {user.firstname} {user.lastname}
                  </Text>
                ));
            })}
        </Flex>
      </Flex>

      {/* Botón toggle + Total */}
      <Divider style={{ margin: "13px 0 11px" }} />
      <Flex justify="space-between" align="center">
        <Text strong style={{ color: token.colorTextSecondary }}>
          Total
        </Text>
        <Text strong>{formattedTotal}</Text>
      </Flex>
    </Flex>
  );
};

export const QuoteCard = React.memo(QuoteCardComponent);
